import { AxiosError } from "axios";
import { ZodError } from "zod";
import { toast } from "react-toastify";

type CustomErrorHandler = (error: AxiosError) => string | void;

interface ErrorHandlerOptions {
  customHandlers?: Partial<Record<number, CustomErrorHandler>>;
}

export function createErrorHandler({
  customHandlers = {},
}: ErrorHandlerOptions = {}) {
  return (error: unknown) => {
    // Handle Axios errors
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      let message: string;

      // Check for custom handler first
      if (status && customHandlers[status]) {
        const customMessage = customHandlers[status]?.(error);
        if (customMessage) {
          message = customMessage;
        } else {
          return; // Custom handler took care of it
        }
      } else {
        // Default messages by status
        switch (status) {
          case 400:
            message = error.response?.data?.error_message;
            error.response?.data?.detail?.msg ||
              "Bad request. Please verify your data.";
            break;
          case 404:
            message = "Resource not found";
            break;
          case 409:
            message = "Conflict: Resource already exists or reached the limit.";
            break;
          case 500:
            message =
              "Internal error occurred. We are working to correct the issue.";
            break;
          default:
            message = "An unexpected error occurred";
        }
      }

      // Display toast for HTTP errors
      toast.error(message);
      return;
    }

    // Handle Zod errors
    if (error instanceof ZodError) {
      if (import.meta.env.DEV) {
        console.error("Zod Validation Error:", error.format());
        throw error;
      }
      return;
    }

    // Fallback for unknown errors
    toast.error("An unknown error occurred");
  };
}
